import DatabaseModel from './DatabaseModel.js';
import { unwrapDto } from './responseUtils.js';

// Normas de vacaciones por versiones: pantalla Ajustes · Normas de vacaciones (#56).
// El backend responde 403 a quien no sea ADMIN o DIRECTOR al publicar y al ver el historial;
// la versión vigente la lee cualquier usuario autenticado, porque es lo que enseñará la ventana
// de solicitud (#58). El gate de verdad está allí: aquí sólo se esconde lo que no se puede usar.
const RhNormasVacaciones = Object.create(DatabaseModel);

RhNormasVacaciones.model = 'rh/normas-vacaciones';
RhNormasVacaciones.modelFront = 'normasvacaciones';

// GET /movilidad/rh/normas-vacaciones/vigente → { version, fechaPublicacion, normas:[...] }
// Devuelve null mientras no haya ninguna versión publicada: no es un error, es el estado del
// sistema antes de lanzar el script de carga de la versión 1.
RhNormasVacaciones.vigente = function () {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/vigente',
        method: 'GET'
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/normas-vacaciones/historial → [{ version, fechaPublicacion, autor, motivo }]
// Sin el texto: el de la versión que se abre con «Ver» se pide aparte.
RhNormasVacaciones.historial = function () {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/historial',
        method: 'GET'
    }, false).then(function (res) {
        return (res && res.list) || [];
    });
};

// GET /movilidad/rh/normas-vacaciones/versiones/{n} → { version, fechaPublicacion, normas:[...] }
RhNormasVacaciones.version = function (numero) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/versiones/'
            + encodeURIComponent(numero),
        method: 'GET'
    }, false).then(unwrapDto);
};

/**
 * GET /movilidad/rh/normas-vacaciones/intentos/{intentoId}
 *   → { intentoId, publicada, version, fechaPublicacion, vigente: { version, normas, ... } }
 *
 * **Qué ha pasado con un intento de publicación**, sin publicar nada. Es la lectura que hace falta
 * cuando el POST no contestó —un 504 de la pasarela, una conexión cortada, un 500— y hay que
 * averiguar si su escritura entró.
 *
 * Trae las DOS mitades porque son dos cosas distintas en cuanto alguien publica por encima:
 * `publicada`/`version` es lo que publicó ESTE intento, y `vigente` es lo que hay en vigor AHORA.
 * `/vigente` a secas no lo contestaba —solo expone la última versión, así que una publicación
 * propia enterrada bajo otra desaparecía de la vista— y el historial no lleva la identidad
 * (revisión CODEX del PR #1963).
 *
 * `publicada` a `false` es «hoy no hay ninguna versión con esa identidad», NO «no la habrá»: un
 * POST que siguiera en curso podría escribirla después. Por eso la pantalla no cierra el intento
 * con esta respuesta: solo la deja volver a comprobar o reenviar el mismo intento.
 */
RhNormasVacaciones.desenlaceIntento = function (intentoId) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/intentos/'
            + encodeURIComponent(intentoId),
        method: 'GET'
    }, false).then(unwrapDto);
};

/**
 * POST /movilidad/rh/normas-vacaciones → el desenlace de este intento (misma forma que
 * `desenlaceIntento`): qué versión publicó él y, aparte, cuál está en vigor ahora.
 *
 * El texto va entero, sin partir en líneas: la regla «una norma por línea» la aplica el backend,
 * que es el mismo sitio donde la aplica el script de carga de la versión 1. Partiéndola aquí,
 * cada cliente tendría su propia idea de qué es una línea vacía.
 *
 * `versionBase` es la versión que tenía delante quien edita, y **0 si no había ninguna publicada**.
 * Si entre cargar la pantalla y pulsar «Publicar» otra persona ha publicado, el backend devuelve
 * 409 en vez de dejar que este texto —que no incluye su cambio— pase como versión nueva. Importa
 * más que en otras pantallas: una versión publicada no se borra (I6), así que el retroceso no se
 * podría deshacer. Por eso va siempre con valor: sin él no hay nada contra lo que comparar.
 *
 * `intentoId` es la **identidad de este intento**: una cadena que no se repite nunca, que el
 * backend guarda en la versión que publique y que devuelve en la vigente y en el historial. Sirve
 * para dos cosas, y las dos hacen falta porque un POST puede escribir y no contestar:
 *
 *   - la pantalla reconoce DESPUÉS su propia publicación por quién escribió el documento, no por
 *     lo que pone: dos personas pueden publicar el mismo texto con motivos distintos;
 *   - un intento que llegue dos veces no publica dos versiones, porque el backend devuelve la que
 *     ese mismo intento ya publicó.
 *
 * Y por eso **un reintento reutiliza la identidad del intento que no se pudo confirmar** en vez de
 * estrenar otra: si la escritura original llegara tarde al servidor, la segunda la reconoce como la
 * misma y no acaba en dos versiones, que no se podrían borrar (I6).
 */
RhNormasVacaciones.publicar = function (texto, motivo, versionBase, intentoId) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model,
        method: 'POST',
        data: { texto: texto, motivo: motivo, versionBase: versionBase, intentoId: intentoId }
    }, false).then(unwrapDto);
};

export default RhNormasVacaciones;
